import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "../../../../utils/demoToast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faForward,
  faTrashAlt,
} from "@fortawesome/pro-light-svg-icons";
import useApi from "../../../../hooks/useApi";
import {
  getDishes,
  updateDish,
  deleteDish,
  getDish,
} from "../../../../services/dishService";
import Modal from "../../../utils/Modal";
import Button from "../../../buttons/Button";
import Spinner from "../../../utils/Spinner";
import Alert from "../../../utils/Alert";
import DishForm from "../dish/DishForm";
import PostAIWizardItem from "./PostAIWizardItem";
import { convertDishToFormData } from "../../../../utils/dishFormUtils";

const PostAIWizard = ({
  isOpen,
  onClose,
  onComplete,
  tKey = "dashboard.products.wizard.",
}) => {
  const { t } = useTranslation();
  const restaurantId = useSelector((state) => state.user.restaurantId);
  const { request: getDishesRequest, loading: dishesLoading } =
    useApi(getDishes);
  const { request: updateDishRequest, loading: saving } = useApi(updateDish);
  const { request: deleteDishRequest, loading: deleting } = useApi(deleteDish);
  const { request: getDishRequest } = useApi(getDish);

  const [sessionDishes, setSessionDishes] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [formData, setFormData] = useState({});
  const [isValid, setIsValid] = useState(false);
  const [reviewedIds, setReviewedIds] = useState(new Set());
  const [deletedIds, setDeletedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");

  const currentDish = sessionDishes[currentIndex];

  const filteredDishes = useMemo(() => {
    if (!searchQuery) return sessionDishes;
    const q = searchQuery.toLowerCase();
    return sessionDishes.filter(
      (d) =>
        d.name?.toLowerCase().includes(q) ||
        d.dishcategory?.label?.toLowerCase().includes(q),
    );
  }, [sessionDishes, searchQuery]);

  const reviewedCount = reviewedIds.size;
  const totalCount = sessionDishes.length;
  const deletedCount = deletedIds.size;
  const progress =
    totalCount > 0 ? (reviewedCount + deletedCount) / totalCount : 0;

  const loadDishIntoForm = useCallback(
    (dish) => {
      if (!dish) return;
      setFormData(convertDishToFormData(dish, t));
    },
    [t],
  );

  const fetchSessionDishes = useCallback(async () => {
    try {
      const res = await getDishesRequest(restaurantId);
      const unpublished = (res.data || []).filter((d) => !d.isPublished);
      setSessionDishes(unpublished);
      if (unpublished.length > 0) {
        loadDishIntoForm(unpublished[0]);
        setCurrentIndex(0);
      }
    } catch (err) {
      console.error(err);
      toast.error(t(`${tKey}fetchError`));
    }
  }, [getDishesRequest, restaurantId, loadDishIntoForm, t, tKey]);

  useEffect(() => {
    if (isOpen) {
      setReviewedIds(new Set());
      setDeletedIds(new Set());
      setSearchQuery("");
      setCurrentIndex(0);
      fetchSessionDishes();
    }
  }, [isOpen, fetchSessionDishes]);

  const handleClose = useCallback(() => {
    if (onComplete) onComplete();
    onClose();
  }, [onClose, onComplete]);

  const navigateTo = useCallback(
    (index) => {
      if (index < 0 || index >= sessionDishes.length) return;
      setCurrentIndex(index);
      loadDishIntoForm(sessionDishes[index]);
    },
    [sessionDishes, loadDishIntoForm],
  );

  const advanceOrClose = useCallback(
    (fromIndex) => {
      if (fromIndex >= sessionDishes.length - 1) {
        handleClose();
        return;
      }
      navigateTo(fromIndex + 1);
    },
    [sessionDishes.length, navigateTo, handleClose],
  );

  const handleFormChange = useCallback((data) => {
    setFormData((prev) => ({
      ...data,
      fullImagePath:
        data.imagePath === null || data.imagePath === undefined
          ? null
          : data.fullImagePath ?? prev.fullImagePath,
    }));
  }, []);

  const handleSaveAndNext = useCallback(async () => {
    if (!currentDish || !isValid) return;
    try {
      await updateDishRequest({
        ...formData,
        isPublished: true,
        isActive: false,
        dishId: currentDish.dishId,
        restaurantId,
      });
      setReviewedIds((prev) => new Set(prev).add(currentDish.dishId));

      const res = await getDishRequest(currentDish.dishId);
      setSessionDishes((prev) =>
        prev.map((d) => (d.dishId === currentDish.dishId ? res.data : d)),
      );

      toast.success(t(`${tKey}saved`, { dishName: currentDish.name }));
      advanceOrClose(currentIndex);
    } catch (err) {
      console.error(err);
      toast.error(err.message);
    }
  }, [
    currentDish,
    isValid,
    formData,
    restaurantId,
    updateDishRequest,
    getDishRequest,
    t,
    tKey,
    advanceOrClose,
    currentIndex,
  ]);

  const handlePublishActivateAndNext = useCallback(async () => {
    if (!currentDish || !isValid) return;
    try {
      await updateDishRequest({
        ...formData,
        isPublished: true,
        isActive: true,
        dishId: currentDish.dishId,
        restaurantId,
      });
      setReviewedIds((prev) => new Set(prev).add(currentDish.dishId));

      const res = await getDishRequest(currentDish.dishId);
      setSessionDishes((prev) =>
        prev.map((d) => (d.dishId === currentDish.dishId ? res.data : d)),
      );

      toast.success(t(`${tKey}published`, { dishName: currentDish.name }));
      advanceOrClose(currentIndex);
    } catch (err) {
      console.error(err);
      toast.error(err.message);
    }
  }, [
    currentDish,
    isValid,
    formData,
    restaurantId,
    updateDishRequest,
    getDishRequest,
    t,
    tKey,
    advanceOrClose,
    currentIndex,
  ]);

  const handleDelete = useCallback(async () => {
    if (!currentDish) return;
    try {
      await deleteDishRequest(currentDish.dishId);
      setDeletedIds((prev) => new Set(prev).add(currentDish.dishId));
      toast.success(t(`${tKey}deleted`, { dishName: currentDish.name }));
      advanceOrClose(currentIndex);
    } catch (err) {
      console.error(err);
      toast.error(err.message);
    }
  }, [currentDish, deleteDishRequest, t, tKey, advanceOrClose, currentIndex]);

  const handleSkip = useCallback(() => {
    advanceOrClose(currentIndex);
  }, [advanceOrClose, currentIndex]);

  useEffect(() => {
    if (!isOpen) return;

    const handler = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA")
        return;

      if (e.key === "ArrowRight") {
        e.preventDefault();
        advanceOrClose(currentIndex);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        if (currentIndex > 0) {
          navigateTo(currentIndex - 1);
        }
      }

      if (e.ctrlKey && e.key === "s") {
        e.preventDefault();
        handleSaveAndNext();
      }
      if (e.ctrlKey && e.key === "Enter") {
        e.preventDefault();
        handlePublishActivateAndNext();
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [
    isOpen,
    currentIndex,
    advanceOrClose,
    navigateTo,
    handleSaveAndNext,
    handlePublishActivateAndNext,
  ]);

  if (!isOpen) return null;

  const isCurrentDeleted = currentDish && deletedIds.has(currentDish.dishId);

  return (
    <Modal
      id="modal-post-ai-wizard"
      isOpen={isOpen}
      onClose={handleClose}
      noFooter
      noBgClickable
    >
      <div className="wizard-header">
        <Button variant="primary" size="sm" onClick={handleClose}>
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          {t(`${tKey}back`)}
        </Button>
        <span className="wizard-header__title">{t(`${tKey}title`)}</span>
        <span className="wizard-header__counter">
          {reviewedCount + deletedCount} / {totalCount}
        </span>
      </div>

      <div className="wizard-progress">
        <div
          className="wizard-progress__bar"
          style={{ width: `${Math.round(progress * 100)}%` }}
        />
      </div>

      <div className="wizard-body">
        <div className="wizard-sidebar">
          <div className="wizard-sidebar__search">
            <input
              className="w-100 p4"
              type="text"
              placeholder={t(`${tKey}searchPlaceholder`)}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="wizard-sidebar__list">
            {dishesLoading ? (
              <Spinner size="sm" />
            ) : filteredDishes.length === 0 ? (
              <p className="p4 text-light text-center mt-3">
                {t(`${tKey}noItems`)}
              </p>
            ) : (
              filteredDishes.map((dish) => {
                const realIndex = sessionDishes.findIndex(
                  (d) => d.dishId === dish.dishId,
                );
                return (
                  <PostAIWizardItem
                    key={dish.dishId}
                    dish={dish}
                    isActive={realIndex === currentIndex}
                    isReviewed={reviewedIds.has(dish.dishId)}
                    isDeleted={deletedIds.has(dish.dishId)}
                    onClick={() => navigateTo(realIndex)}
                  />
                );
              })
            )}
          </div>
        </div>

        <div className="wizard-editor">
          {dishesLoading ? (
            <div className="flex justify-center align-center flex-grow">
              <Spinner
                size="3x"
                variant="danger"
                label={t("utils.data.loading")}
              />
            </div>
          ) : totalCount === 0 ? (
            <div className="flex column justify-center align-center flex-grow p-5">
              <Alert variant="pastel-info">
                {t(`${tKey}noDishesToReview`)}
              </Alert>
              <Button variant="primary" size="sm" onClick={handleClose}>
                {t(`${tKey}back`)}
              </Button>
            </div>
          ) : isCurrentDeleted ? (
            <div className="flex column justify-center align-center flex-grow p-5">
              <Alert variant="pastel-warning">{t(`${tKey}dishDeleted`)}</Alert>
              <Button variant="secondary" size="sm" onClick={handleSkip}>
                {t(`${tKey}next`)}
              </Button>
            </div>
          ) : (
            <>
              <div className="wizard-editor__form">
                <Alert variant="pastel-info" className="mb-3">
                  <span className="bold">{currentDish?.name}</span>
                  {" — "}
                  {t(`${tKey}editHint`)}
                </Alert>
                <DishForm
                  formData={formData}
                  dishId={currentDish?.dishId}
                  onChange={handleFormChange}
                  setIsValid={setIsValid}
                  imgPreview
                  fieldClass="wizard-field"
                />
              </div>
              <div className="wizard-editor__actions">
                <div className="wizard-editor__actions-left">
                  <Button
                    variant="danger"
                    size="sm"
                    outlined
                    onClick={handleDelete}
                    loading={deleting}
                    disabled={saving}
                  >
                    <FontAwesomeIcon icon={faTrashAlt} className="mr-2" />
                    {t(`${tKey}delete`)}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleSkip}
                    disabled={saving || deleting}
                  >
                    <FontAwesomeIcon icon={faForward} className="mr-2" />
                    {t(`${tKey}skip`)}
                  </Button>
                </div>
                <div className="wizard-editor__actions-right">
                  <Button
                    size="sm"
                    onClick={handleSaveAndNext}
                    loading={saving}
                    disabled={!isValid || deleting}
                  >
                    {t(`${tKey}btnPublishNext`)}
                  </Button>
                  <Button
                    variant="success"
                    size="sm"
                    onClick={handlePublishActivateAndNext}
                    loading={saving}
                    disabled={!isValid || deleting}
                  >
                    {t(`${tKey}btnPublishActivateNext`)}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="wizard-footer">
        <span>
          <kbd>&larr;</kbd> <kbd>&rarr;</kbd> {t(`${tKey}kbNavigate`)}
        </span>
        <span>
          <kbd>Ctrl+S</kbd> {t(`${tKey}kbSave`)}
        </span>
        <span>
          <kbd>Ctrl+Enter</kbd> {t(`${tKey}kbPublish`)}
        </span>
        <span>
          <kbd>Esc</kbd> {t(`${tKey}kbClose`)}
        </span>
      </div>
    </Modal>
  );
};

export default PostAIWizard;
